import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Check, Mail, Calendar, Users, Clock, AlertTriangle, Sparkles, CheckCircle } from 'lucide-react';
import PillarLayout from '../pillar/PillarLayout';

const CANONICAL = 'https://enbet-consulting.de/ki-masterclass';
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Fallback wenn API nicht erreichbar (Build/SSR)
const FALLBACK = {
  title: 'KI-Masterclass für Betreiber & Entscheider',
  subtitle: 'Vom Quick Win zur sicheren Unternehmens-Infrastruktur',
  hook_quote: 'Die teuerste Illusion im B2B-Sektor: „Wir nutzen ja schon KI."',
  date_label: 'Termin in Planung',
  time_label: '',
  format_label: 'Format wird bekanntgegeben',
  location_label: '',
  price_label: 'Preis folgt',
  max_seats: 100,
  status: 'draft',
  hero_image_url: '/assets/masterclass-hero.jpg',
  cta_image_url: '/assets/masterclass-cta.jpg',
  signup_open: true,
};

const blocks = [
  {
    n: '01', title: 'Effizienz',
    sub: 'Die 6 Säulen mit echten Best Cases aus dem Mittelstand',
    detail: 'Operations, Customer Service, Sales, HR, Finance, Supply Chain — sechs Bereiche, in denen KI wirklich messbare Effekte bringt. Du siehst replizierbare Architekturen aus echten Mittelstands-Projekten.',
  },
  {
    n: '02', title: 'Anatomie des Scheiterns',
    sub: 'Warum 85 % aller KI-Projekte den Produktionsbetrieb nie erreichen',
    detail: 'Die vier strukturellen Fehlerfaktoren, an denen die meisten KI-Initiativen sterben. Du lernst sie zu erkennen — bevor sie dich treffen.',
  },
  {
    n: '03', title: 'Compliance & Recht',
    sub: 'DSGVO, EU AI Act und die persönliche Betreiberhaftung',
    detail: 'Was unter dem EU AI Act in welche Risikoklasse fällt, wann eine AVV Pflicht ist und wo die persönliche Haftung der Geschäftsführung beginnt — verständlich, operativ, ohne Juristen-Latein.',
  },
  {
    n: '04', title: 'Festung & Strategie',
    sub: 'Das hybride Sicherheitsmodell und die strategische Roadmap',
    detail: 'Wie du lokale Datenhoheit nach deutschem Recht mit der Rechenpower globaler KI-Marktführer verbindest. Plus: die strukturierte Roadmap von Status-Quo-Analyse bis produktiver KI-Infrastruktur.',
  },
];

const audience = [
  { t: 'Geschäftsführer mittelständischer Betriebe', s: 'die Verantwortung tragen, wenn ein KI-Projekt scheitert oder eine DSGVO-Prüfung kommt.' },
  { t: 'IT- und Operations-Verantwortliche', s: 'die zwischen "wir müssen schnell" und "wir müssen sicher" entscheiden müssen.' },
  { t: 'Compliance- und Datenschutz-Verantwortliche', s: 'die EU AI Act und DSGVO operativ übersetzen müssen, statt im Theoriewald zu bleiben.' },
  { t: 'Entscheider, die schon KI einsetzen', s: 'und wissen wollen, ob die aktuelle Lösung Bestand hat — oder eine tickende Zeitbombe ist.' },
];

const faqItems = [
  {
    q: 'Was bekomme ich konkret aus 3 Stunden Masterclass mit?',
    a: 'Drei Dinge: (1) Eine ehrliche Diagnose, wo dein Betrieb in Sachen KI-Reife wirklich steht. (2) Eine klare Sicht auf die vier häufigsten Stolperfallen, an denen 85 % der KI-Projekte scheitern. (3) Den strategischen Rahmen, mit dem du KI sicher, compliant und mit messbarem ROI einführen kannst — ohne Tool-Verkäufer-Folklore.',
  },
  {
    q: 'Welches Vorwissen brauche ich?',
    a: 'Keines. Die Masterclass ist explizit für Entscheider gemacht, nicht für Entwickler. Du musst kein LLM von innen verstehen — du musst verstehen, welche strategischen Entscheidungen du als Geschäftsleitung treffen musst, und welche du delegieren kannst.',
  },
  {
    q: 'Ist das Werbeveranstaltung für ENBET-Beratung?',
    a: 'Nein. Die Masterclass ist eine eigenständige Wissens-Session — keine 3-Stunden-Sales-Show. Du bekommst konkrete Methoden mit nach Hause, die du am Tag danach selbst anwenden kannst. Wer am Ende ein ENBET-Strategie-Gespräch will, bekommt ein begrenztes Angebot. Wer nur Wissen wollte, hat dieses voll bekommen.',
  },
  {
    q: 'Bekomme ich Unterlagen?',
    a: 'Ja. Teilnehmer erhalten nach der Session die zentralen Frameworks als PDF-Handout sowie Zugriff auf das Q&A-Protokoll der Live-Fragen. Das Live-Erleben (Demos, Diskussion, Interaktion) lässt sich nicht in Folien packen — die Folien sind die Erinnerungsstütze.',
  },
  {
    q: 'Wie viele Plätze gibt es?',
    a: 'Die Masterclass ist auf 50–100 Entscheider ausgelegt. Das ist bewusst klein gehalten, damit die Live-Interaktionen (Voting, Compliance-Quiz, Live-Demo, Q&A) auch wirklich funktionieren. Großgruppen-Webinare gibt es genug — diese hier ist eine Arbeitssitzung.',
  },
];

function SignupForm({ disabled, eventId }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // UTM + Referrer aus URL/Document ziehen (datenschutzkonform: eigene Tracking-Parameter, keine externen Tracker)
  const trackingPayload = (() => {
    if (typeof window === 'undefined') return {};
    const params = new URLSearchParams(window.location.search);
    let refPath = '';
    try {
      const ref = document.referrer || '';
      if (ref && ref.startsWith(window.location.origin)) {
        refPath = ref.replace(window.location.origin, '').slice(0, 200);
      }
    } catch { /* noop */ }
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      referrer_path: refPath,
    };
  })();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name und E-Mail sind Pflicht.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/masterclass/signup`, {
        ...form,
        event_id: eventId || undefined,
        ...trackingPayload,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen — bitte später erneut versuchen oder direkt per Mail.');
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div className="bg-[#befa0a]/10 border border-[#befa0a]/40 p-8 text-center">
        <CheckCircle size={36} className="text-[#658a07] mx-auto mb-4" />
        <h3 className="text-[#1f1f1f] text-xl md:text-2xl font-bold uppercase mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Anmeldung eingegangen!
        </h3>
        <p className="text-[#1f1f1f]/70 text-base max-w-md mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Sebastian meldet sich persönlich mit der Anmeldebestätigung sowie allen Eckdaten zum Termin. Schau auch in dein Spam-Postfach.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input type="text" required placeholder="Name *" value={form.name} disabled={disabled || submitting}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="w-full bg-white border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50"
          style={{ fontFamily: "'Manrope', sans-serif" }} />
        <input type="email" required placeholder="E-Mail *" value={form.email} disabled={disabled || submitting}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          className="w-full bg-white border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50"
          style={{ fontFamily: "'Manrope', sans-serif" }} />
        <input type="text" placeholder="Unternehmen" value={form.company} disabled={disabled || submitting}
          onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
          className="w-full bg-white border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50"
          style={{ fontFamily: "'Manrope', sans-serif" }} />
        <input type="text" placeholder="Funktion (z.B. Geschäftsführung)" value={form.role} disabled={disabled || submitting}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
          className="w-full bg-white border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50"
          style={{ fontFamily: "'Manrope', sans-serif" }} />
      </div>
      <textarea placeholder="Nachricht (optional) — z.B. was du dir konkret aus der Masterclass mitnehmen willst" value={form.message}
        rows={3} disabled={disabled || submitting}
        onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
        className="w-full bg-white border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] resize-none disabled:opacity-50"
        style={{ fontFamily: "'Manrope', sans-serif" }} />
      {error && (
        <p className="text-red-600 text-sm flex items-center gap-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <AlertTriangle size={14} /> {error}
        </p>
      )}
      <button type="submit" disabled={disabled || submitting}
        className="w-full bg-[#befa0a] text-[#0a0a0a] py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:border hover:border-[#befa0a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ fontFamily: "'Oswald', sans-serif" }}>
        {submitting ? 'Sende…' : 'Platz sichern'}
      </button>
      <p className="text-[#1f1f1f]/40 text-[11px] text-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
        Mit dem Klick auf „Platz sichern" stimmst du der Verarbeitung deiner Daten für die Anmeldebestätigung zu (DSGVO).
      </p>
    </form>
  );
}

export default function LandingMasterclass() {
  const [event, setEvent] = useState(FALLBACK);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/masterclass`).then((r) => {
      if (!cancelled && r.data) setEvent({ ...FALLBACK, ...r.data });
    }).catch(() => {/* Fallback bleibt */});
    return () => { cancelled = true; };
  }, []);

  const isPast = event.status === 'past';
  const isSoldOut = event.status === 'sold_out';
  const isLive = event.status === 'live';
  const signupVisible = isLive && event.signup_open && !isSoldOut;

  const schemaLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Event',
        '@id': `${CANONICAL}#event`,
        name: event.title || 'ENBET KI-Masterclass',
        description: event.subtitle || '3-stündige Intensiv-Session für 50–100 Entscheider.',
        eventStatus: isPast ? 'https://schema.org/EventCancelled' : 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
        organizer: { '@id': 'https://enbet-consulting.de/#organization' },
        url: CANONICAL,
        inLanguage: 'de-DE',
        about: 'KI-Strategie, Compliance, EU AI Act, DSGVO, KI-Infrastruktur',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Start', item: 'https://enbet-consulting.de/' },
          { '@type': 'ListItem', position: 2, name: 'KI-Masterclass', item: CANONICAL },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <PillarLayout
      title={`${event.title} | ENBET Consulting`}
      description={`${event.subtitle} · ${event.date_label} · ${event.format_label}`}
      canonical={CANONICAL}
      schemaLd={schemaLd}
    >
      {/* Hero */}
      <section className="bg-[#0a0a0a] pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-5 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                ENBET Consulting · Masterclass
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
                className="text-white text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[1.05] mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {event.title}
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {event.subtitle}
              </motion.p>

              {event.hook_quote && (
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                  className="border-l-2 border-[#befa0a] pl-5 mb-8 max-w-2xl">
                  <p className="text-[#befa0a] text-base md:text-lg italic leading-snug" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {event.hook_quote}
                  </p>
                </motion.div>
              )}

              {/* Event-Eckdaten */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <div className="border border-white/15 px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[#befa0a] text-[10px] uppercase tracking-wider font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <Calendar size={12} /> Termin
                  </div>
                  <p className="text-white text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {event.date_label}{event.time_label ? ` · ${event.time_label}` : ''}
                  </p>
                </div>
                <div className="border border-white/15 px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[#befa0a] text-[10px] uppercase tracking-wider font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <Clock size={12} /> Format
                  </div>
                  <p className="text-white text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>{event.format_label || '3 Stunden'}</p>
                </div>
                <div className="border border-white/15 px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[#befa0a] text-[10px] uppercase tracking-wider font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <Users size={12} /> Plätze
                  </div>
                  <p className="text-white text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {event.seats_free != null ? `${event.seats_free} frei` : `${event.max_seats || '50–100'}`}
                  </p>
                </div>
                <div className="border border-[#befa0a]/40 bg-[#befa0a]/5 px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[#befa0a] text-[10px] uppercase tracking-wider font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    <Sparkles size={12} /> Investment
                  </div>
                  <p className="text-[#befa0a] text-sm font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>{event.price_label}</p>
                </div>
              </motion.div>

              {/* Hero-CTA */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}>
                <a href="#anmeldung"
                  className="inline-flex items-center justify-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {signupVisible ? 'Platz sichern' : isSoldOut ? 'Warteliste' : 'Mehr erfahren'} <ArrowRight size={16} />
                </a>
                {isPast && (
                  <p className="text-white/50 text-sm mt-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Diese Masterclass ist vorbei — nimm Kontakt auf für den nächsten Termin.
                  </p>
                )}
                {!signupVisible && !isPast && (
                  <p className="text-white/50 text-sm mt-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Anmeldung folgt — schreib uns vorab, wenn du dabei sein willst.
                  </p>
                )}
              </motion.div>
            </div>

            {/* Visual */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-5">
              <div className="relative aspect-[9/16] max-w-[400px] mx-auto overflow-hidden border-2 border-[#befa0a]/20">
                <img src={event.hero_image_url} alt={event.title} className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pain-Block */}
      <section className="bg-[#f7f7f7] py-16 md:py-20">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Status Quo
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-[1.15] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Werkzeuge ohne Methode vergrößern das operative Chaos.
          </h2>
          <p className="text-[#1f1f1f]/75 text-base md:text-lg leading-relaxed mb-10 max-w-3xl" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Drei strukturelle Blockaden ziehen sich heute durch fast jeden Mittelstandsbetrieb. Diese Masterclass adressiert genau diese drei — direkt, mit Beispielen, mit operativen Antworten.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { t: 'Administrative Erschöpfung', s: 'Wertvolle Fachkräfte verbringen ihren Tag mit Mail-Sortieren, PDF-Lesen und Copy-Paste — statt mit Wertschöpfung.' },
              { t: 'Rechtliche Paralyse', s: 'Angst vor DSGVO und EU AI Act blockiert Entscheidungen. Wer abwartet, verliert — Wettbewerber handeln.' },
              { t: 'Schatten-IT & Wildwuchs', s: 'Mitarbeiter nutzen private Consumer-Tools mit Firmendaten. Eine tickende DSGVO-Zeitbombe, die kein Geschäftsführer auf dem Schirm hat.' },
            ].map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white border border-[#e5e5e5] p-6">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle size={18} className="text-[#e07a2f] flex-shrink-0 mt-0.5" />
                  <h3 className="text-[#1f1f1f] text-base md:text-lg font-bold uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>{p.t}</h3>
                </div>
                <p className="text-[#1f1f1f]/70 text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{p.s}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 Blöcke */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Was du mitnimmst
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Vier Blöcke, drei Stunden, eine Entscheidungsgrundlage.
          </h2>
          <p className="text-[#1f1f1f]/60 text-base md:text-lg max-w-3xl mb-12" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Die Session ist kein lineares Webinar. Jeder Block ist ein abgeschlossener Themen-Komplex mit Live-Interaktion, Best Cases und Q&amp;A.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {blocks.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-[#e5e5e5] hover:border-[#befa0a] transition-colors p-6 md:p-8">
                <div className="flex items-baseline gap-4 mb-3">
                  <span className="text-[#befa0a] text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{b.n}</span>
                  <h3 className="text-[#1f1f1f] text-xl md:text-2xl font-bold uppercase tracking-tight leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    Block {b.n.replace(/^0/, '')}: {b.title}
                  </h3>
                </div>
                <p className="text-[#658a07] text-sm md:text-base italic font-medium leading-snug mb-4 border-l-2 border-[#befa0a] pl-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {b.sub}
                </p>
                <p className="text-[#1f1f1f]/70 text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{b.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Für wen */}
      <section className="bg-[#f5f5f5] py-16 md:py-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Für wen
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Du erkennst dich in einem dieser vier Profile?
          </h2>
          <div className="space-y-6">
            {audience.map((a, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="flex items-start gap-4 bg-white border border-[#e5e5e5] p-5">
                <Check size={20} className="text-[#befa0a] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-[#1f1f1f] text-base md:text-lg font-bold uppercase tracking-tight mb-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}>{a.t}</h3>
                  <p className="text-[#1f1f1f]/65 text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{a.s}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sebastian-Block */}
      <section className="bg-[#0a0a0a] py-16 md:py-24 overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative aspect-[9/16] max-w-[380px] mx-auto overflow-hidden border-2 border-[#befa0a]/20">
                <img src={event.cta_image_url} alt="Sebastian Funke" className="w-full h-full object-cover" />
              </div>
            </motion.div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Vom Zuschauer zum Gestalter
              </p>
              <h2 className="text-white text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Schluss mit teuren KI-Fehlern.
              </h2>
              <p className="text-white/75 text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Sebastian Funke führt durch alle vier Blöcke. Hintergrund: 100+ skalierte Retail-Standorte, ca. 9 Mio. Monatsumsatz im Retail-Setup, AI Business Strategy Weiterbildung 2025. Keine Buzzwords, keine Folien-Lyrik — operative Antworten auf operative Fragen.
              </p>
              <p className="text-white/60 text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Sichern Sie sich Ihren Platz in der Masterclass und führen Sie Ihr Unternehmen sicher durch die KI-Transformation — bevor Wettbewerber den Vorsprung haben.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Anmelde-Block */}
      <section id="anmeldung" className="bg-white py-16 md:py-24 scroll-mt-24">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4 text-center" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {signupVisible ? 'Platz sichern' : isSoldOut ? 'Warteliste' : isPast ? 'Nächste Masterclass' : 'Vormerken lassen'}
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-4 text-center" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {signupVisible ? 'Jetzt anmelden.' : isSoldOut ? 'Auf die Warteliste.' : 'Schreib uns kurz.'}
          </h2>
          <p className="text-[#1f1f1f]/65 text-base md:text-lg text-center mb-10 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {signupVisible
              ? `Trag dich ein, dann bekommst du Termin-Details und die finale Anmeldebestätigung direkt von Sebastian.`
              : isSoldOut
              ? 'Diese Runde ist ausgebucht — auf der Warteliste bekommst du den nächsten Termin als Erstes.'
              : isPast
              ? 'Diese Masterclass ist gelaufen. Melde dich, wir informieren dich über den nächsten Termin.'
              : 'Termin und Anmeldung kommen in Kürze. Lass dich vormerken — wir schicken dir die Anmelde-Mail als Erstes.'}
          </p>

          {signupVisible ? (
            <SignupForm disabled={false} eventId={event.id} />
          ) : (
            <div className="text-center">
              <a href={`mailto:info@enbet-consulting.de?subject=${encodeURIComponent(isPast ? 'KI-Masterclass – Nächster Termin' : isSoldOut ? 'KI-Masterclass – Warteliste' : 'KI-Masterclass – Vormerken lassen')}&body=${encodeURIComponent('Hi Sebastian,\n\nbitte merk mich für die nächste KI-Masterclass vor.\n\nName: \nUnternehmen: \nFunktion: \n\nVielen Dank!')}`}
                className="inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#befa0a] hover:text-[#0a0a0a] transition-colors"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                <Mail size={16} /> E-Mail schreiben
              </a>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#f5f5f5] py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Häufige Fragen
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Was du wahrscheinlich noch fragst.
          </h2>
          <div className="space-y-8">
            {faqItems.map((f, i) => (
              <div key={i}>
                <h3 className="text-[#1f1f1f] text-lg md:text-xl font-bold uppercase tracking-tight mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>{f.q}</h3>
                <p className="text-[#1f1f1f]/70 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weiterführende Links */}
      <section className="py-12 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#1f1f1f]/50 text-xs uppercase tracking-[0.25em] font-bold mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Mehr von ENBET
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              { to: '/ki-ready-check', label: 'KI Ready Check (Modul 1)' },
              { to: '/ki-strategie-workshop', label: 'KI Strategie Workshop (Modul 2)' },
              { to: '/ki-im-stationaeren-handel', label: 'KI im stationären Handel' },
              { to: '/ki-beratung-kassel', label: 'KI-Beratung Kassel' },
            ].map((l, i) => (
              <Link key={i} to={l.to}
                className="inline-flex items-center gap-2 border border-[#e5e5e5] text-[#1f1f1f]/70 hover:border-[#befa0a] hover:text-[#1f1f1f] px-4 py-2 text-xs uppercase tracking-wider transition-colors"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {l.label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PillarLayout>
  );
}
